import { styled } from '@linaria/react';
import { Group, Modal, Stack, Textarea, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';
import {
  FLORA_FEEDBACK_COMMENT_MAX_LENGTH,
  FLORA_FEEDBACK_REASON_OPTIONS,
  FloraFeedbackReason,
  FloraFeedbackSentiment,
} from '../../../../api/flora-feedback-client/flora-feedback-client.type';
import { Button } from '../../../../ui-library/button/button';
import { Chip } from '../../../../ui-library/chip/chip';
import { Text } from '../../../../ui-library/typography/typography';

/**
 * Values collected by the feedback modal and handed back to the parent on submit.
 */
interface FloraFeedbackModalValue {
  sentiment: FloraFeedbackSentiment;
  reasons: FloraFeedbackReason[];
  comment: string;
}

interface FloraFeedbackModalProps {
  opened: boolean;
  sentiment: FloraFeedbackSentiment;
  isSubmitting: boolean;
  onSubmit: (value: FloraFeedbackModalValue) => void;
  onClose: () => void;
}

/**
 * Modal for rating a Flora response. The sentiment is fixed to whichever thumb
 * opened it — to rate the other way the user closes (X) and clicks the other
 * thumb. Negative ratings show reason pills (multiselect, one per line) and
 * require at least one before submitting; positive ratings carry no reasons. A
 * free-text comment is always optional.
 *
 * @param {FloraFeedbackModalProps} props - Component props.
 * @param {boolean} props.opened - Whether the modal is open.
 * @param {FloraFeedbackSentiment} props.sentiment - The rating sentiment (the clicked thumb); not changeable in the modal.
 * @param {boolean} props.isSubmitting - Whether a submit is in flight (disables the form + Submit).
 * @param {(value: FloraFeedbackModalValue) => void} props.onSubmit - Called with the collected feedback on Submit.
 * @param {() => void} props.onClose - Called when the modal is dismissed (X / overlay / Esc).
 * @returns {JSX.Element} The feedback modal.
 */
export const FloraFeedbackModal = ({ opened, sentiment, isSubmitting, onSubmit, onClose }: FloraFeedbackModalProps) => {
  const [selectedReasons, setSelectedReasons] = useState<FloraFeedbackReason[]>([]);
  const [comment, setComment] = useState('');

  // Reset the form each time the modal is (re)opened so a previous draft never leaks in.
  useEffect(() => {
    if (opened) {
      setSelectedReasons([]);
      setComment('');
    }
  }, [opened]);

  const isNegative = sentiment === FloraFeedbackSentiment.Negative;

  const setReasonChecked = (reason: FloraFeedbackReason, checked: boolean) => {
    setSelectedReasons((prev) => (checked ? [...prev, reason] : prev.filter((r) => r !== reason)));
  };

  const needsReason = isNegative && selectedReasons.length === 0;
  const submitDisabled = isSubmitting || needsReason;
  const isFaded = submitDisabled && !isSubmitting;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={460}
      padding={24}
      title={isNegative ? 'What went wrong?' : 'Positive Feedback'}
      closeButtonProps={{ 'aria-label': 'Close feedback' }}
      styles={{
        content: { borderRadius: 12, boxShadow: 'var(--box-shadow-big-soft)' },
        title: { fontFamily: 'Figtree-bold', fontWeight: 700, fontSize: 'var(--font-size-large)' },
        // Mantine's modal header has a default min-height that left a dead gap under the title;
        // drop it and tighten the bottom padding so the body sits close to the title.
        header: { minHeight: 'auto', paddingBottom: 12 },
        body: { paddingTop: 0 },
      }}
    >
      <Stack gap={16}>
        {isNegative && (
          <ReasonsSection>
            <Text id="flora-feedback-reasons-hint" size="small" color="var(--ink-light)">
              Select all that apply
            </Text>
            <ReasonsList role="group" aria-labelledby="flora-feedback-reasons-hint">
              {FLORA_FEEDBACK_REASON_OPTIONS.map(({ value, label }) => (
                <Chip
                  key={value}
                  size="sm"
                  radius="lg"
                  checked={selectedReasons.includes(value)}
                  onChecked={(checked) => setReasonChecked(value, checked)}
                  data-testid={`flora-feedback-reason-${value}`}
                  role="checkbox"
                  aria-checked={selectedReasons.includes(value)}
                  aria-label={label}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setReasonChecked(value, !selectedReasons.includes(value));
                    }
                  }}
                >
                  {label}
                </Chip>
              ))}
            </ReasonsList>
          </ReasonsSection>
        )}

        <Textarea
          placeholder="Add a comment (optional)"
          aria-label="Add a comment (optional)"
          value={comment}
          onChange={(event) => setComment(event.currentTarget.value)}
          maxLength={FLORA_FEEDBACK_COMMENT_MAX_LENGTH}
          autosize
          minRows={2}
          maxRows={6}
        />

        <Group justify="flex-end">
          <Tooltip
            label="Select at least one reason to submit"
            disabled={!needsReason}
            position="top"
            withinPortal={false}
          >
            {/* Wrapper receives hover even while the button is disabled, so the tooltip can show. */}
            <SubmitWrap data-testid="flora-feedback-submit-wrap">
              <Button
                radius="xl"
                onClick={() => onSubmit({ sentiment, reasons: selectedReasons, comment })}
                loading={isSubmitting}
                disabled={submitDisabled}
                style={isFaded ? { backgroundColor: 'var(--sky-base)', pointerEvents: 'none' } : undefined}
              >
                <Text weight="medium" color={isFaded ? 'var(--ink-light)' : 'var(--base-white)'}>
                  Submit
                </Text>
              </Button>
            </SubmitWrap>
          </Tooltip>
        </Group>
      </Stack>
    </Modal>
  );
};

const ReasonsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const ReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const SubmitWrap = styled.span`
  display: inline-flex;
`;
